import { VenueBasedEventSerializer } from "@app-contexts/dashboard/features/events/data/types/events";
import { PublicVenueSerializer } from "@app-contexts/dashboard/features/venues/data/types/venues";
import { UserBasedDiscountTicketInfoSerializer } from "@app-contexts/user/features/discounts/data/types";
import Skeleton from "@shared/components/ui-kit/feedback/Skeleton/Skeleton.web";
import Flex from "@shared/components/ui-kit/layout/Flex/Flex.web";
import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { useAnalyticsContext } from "@shared/contexts/AnalyticsContext/useAnalyticsContext";
import { X_SP_UUID } from "@shared/env";
import useDebounce from "@shared/hooks/useDebounce";
import useAnalytics from "@shared/lib/analytics/hooks/useAnalytics";
import UserInteractionLogger from "@shared/lib/analytics/internal/UserInteractionLogger";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { getQuantityForTicketType } from "@shared/modules/basket/utils/ticket-basket-utils";
import ItemPasswordEntry from "@shared/modules/events/components/ItemPasswordEntry/ItemPasswordEntry.web";
import { PUBLIC_ROUTES } from "@app-contexts/public/constants/public-routes";
import {
  displayTicketType,
  showTicketButtons,
  trackItemsInCart,
} from "@shared/modules/events/utils/detail-page-helpers";
import {
  isSoldOutEvent,
  isTicketOrEventSoldOut,
} from "@shared/modules/events/utils/event-utils";
import {
  isOnSaleTicketType,
  isTicketTypeSoldOut,
  isTicketTypeWaitlisted,
  voucherForTT,
} from "@shared/modules/events/utils/ticket-type-utils";
import { PwycPriceManager } from "@shared/modules/seating/services/PwycPriceManager";
import { componentName } from "@shared/utils/bem";
import PaymentVerbiage from "@shared/modules/ticket-types/components/PaymentVerbiage/PaymentVerbiage.web";
import { logAppInfo, recordAppError } from "@shared/utils/errors/errorLogger";
import InventoryBadge from "@ui-application/data-display/badges/InventoryBadge/InventoryBadge.web";
import SaleStartsOnBadge from "@ui-application/data-display/badges/SaleStartsOnBadge/SaleStartsOnBadge.web";
import ItemQuantitySelectInfo from "@ui-application/data-display/ItemQuantitySelectInfo/ItemQuantitySelectInfo.web";
import DetailCardContainer from "@ui-kit/data-display/DetailCardContainer/DetailCardContainer.web";
import Box from "@ui-kit/layout/Box/Box.web";
import ReadMore from "@ui-kit/typography/ReadMore/ReadMore.web";
import { useTranslation } from "@shared/utils/internationalization";
import { useCallback, useEffect, useRef, useState } from "react";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import {
  PublicBaseEventSerializer,
  PublicCalendarEventDetailsSerializer,
  TicketTypeSerializer,
} from "../../../event-detail/data/types/events";
import StripeAffirmText from "../StripeAffirmText/StripeAffirmText.web";

const pwycPriceManager = PwycPriceManager.getInstance();

export interface TicketTypesContainerProps {
  eventData:
    | PublicBaseEventSerializer
    | PublicCalendarEventDetailsSerializer
    | VenueBasedEventSerializer;
  venue: PublicBaseEventSerializer["venue"] | PublicVenueSerializer;
  tt: TicketTypeSerializer;
  isLoading: boolean;
  isRecurring: boolean;
  ticketsAuthorizedByPassword: number[];
  ticketAuthArray: { id: number; authorized: boolean }[];
  enableAffirm?: boolean;
  updateTicketAuthArray: (
    input: Array<{ id: number; authorized: boolean }>
  ) => void;
  basketVoucherCode: string | null;
  userVouchers: UserBasedDiscountTicketInfoSerializer[];
  isCalendar?: boolean;
  isModal?: boolean;
  dynamicHoldSlug?: string;
  quantityButtonsAlwaysBelow?: boolean;
  purchaseLimits?: {
    min?: number;
    max?: number;
  };
  /**
   * On mount, the amount requested is used to diplay the quantity.
   * By default, this component looks at the basket to determine the amount requested.
   * This parameter overrides that and explicitly sets the amount.
   */
  initialAmountRequested?: number;
  /**
   * By default, this component performs a basket update after the quantity change debounce mechanism completes.
   * This parameter overrides that mechanism *completely* (including post-add to cart logic, like analytics).
   */
  onAmountRequested?: (
    tt: TicketTypeSerializer,
    amountRequested: number
  ) => void;
  /**
   * For TTs with patron_type.
   *
   * When true, we display the UI a bit differently
   */
  isPatronItemSelection?: boolean;
  /**
   * For TTs with patron_type.
   *
   * A starting from price that we compare _this_ tt's price to
   * in order to display a price difference.
   *
   * Requires `isPatronItemSelection` to be true.
   */
  startingFromPrice?: string;
  /**
   * The number of ticket types that have a patron type.
   */
  ticketTypesPatronTypeCount?: number;
  /**
   * If true, the quantity buttons will be displayed regardless. This is so ticket types with seats dont show "view seats" button.
   */
  forceQuantityButtons?: boolean;
  withDebounce?: boolean;
  enableTracking?: boolean;
  hideDescription?: boolean;
  itemQuantitySelectInfoChildren?: React.ReactNode;
  titleAccessory?: React.ReactNode;
  onCustomPackageSelect?: (ticketTypeId: number, quantity: number) => void;
  onEventSelectCallback?: () => void;
}
/**
 * TODO: A major refactor and cleanup is needed here.
 */
const TicketTypesContainer = ({
  eventData,
  venue,
  tt,
  isLoading,
  isRecurring,
  ticketsAuthorizedByPassword,
  ticketAuthArray,
  updateTicketAuthArray,
  basketVoucherCode,
  userVouchers,
  isCalendar = undefined,
  isModal = undefined,
  dynamicHoldSlug = undefined,
  purchaseLimits = undefined,
  initialAmountRequested = undefined,
  onAmountRequested,
  isPatronItemSelection,
  startingFromPrice,
  ticketTypesPatronTypeCount,
  forceQuantityButtons = false,
  quantityButtonsAlwaysBelow = false,
  withDebounce = true,
  enableAffirm = true,
  enableTracking = true,
  hideDescription = false,
  itemQuantitySelectInfoChildren,
  titleAccessory,
  onCustomPackageSelect,
  onEventSelectCallback,
}: TicketTypesContainerProps) => {
  const { t } = useTranslation("common");
  const { enableAnalytics } = useAnalyticsContext();
  const analytics = useAnalytics();
  const { SWITCHES } = useDjangoWaffle();
  const { itemGroups, purchaseSourcePlatform, modifyItems } =
    useBasketService();

  const hasPaymentPlans =
    tt.has_payment_plans || (tt.payment_plans && tt.payment_plans.length > 0);

  // consolidates fields needed for custom package display
  const getCustomPackageData = () => {
    const ticketTypeIsCustomPackage = Boolean(tt.is_custom_package);
    const quantityInBasket = getQuantityForTicketType(itemGroups, tt.id);

    return {
      hasPackage: ticketTypeIsCustomPackage,
      canEdit: Boolean(quantityInBasket) && ticketTypeIsCustomPackage,
      quantity: quantityInBasket,
    };
  };

  const openFrontendDetailsUrl = (opts?: { ticketTypeId?: number }) => {
    let eventUrl = PUBLIC_ROUTES.EVENTS.detail(eventData.slug);

    if (opts?.ticketTypeId) {
      eventUrl = `${eventUrl}?ticket-type=${opts.ticketTypeId}`;
    }

    window.open(eventUrl, "_self");
  };

  const ticketsInBasket = itemGroups.filter((ig) => ig.type === tt.id);
  const ticketsInBasketQuantity = ticketsInBasket
    ? ticketsInBasket.reduce((prev, curr) => {
        // Since child item_groups (has a non-nullish parent) are hidden in the cart,
        // We follow the same principle for counting quantities
        if (!curr.parent) return prev + curr.quantity;

        return prev;
      }, 0)
    : 0;

  // is_available_for_quantity_requested=false means "no more can be added for the
  // requested quantity" — not that the ticket is globally sold out. When the user
  // already holds inventory in their basket, suppress the sold-out treatment so the
  // quantity controls remain usable (they can still decrease).
  const isSoldOut =
    isTicketOrEventSoldOut(tt, eventData) &&
    (tt.sold_out || isSoldOutEvent(eventData) || ticketsInBasketQuantity === 0);

  // This controls the quantity amount requested to the API, changed by this component only.
  const [amountRequested, setAmountRequested] = useState({
    amount: initialAmountRequested ?? ticketsInBasketQuantity,
  });

  const [debouncedAmountRequested] = useDebounce(amountRequested, 500);

  /** get if item in the basket has pwyc price */
  const customPrice =
    itemGroups.find(
      (ig) => ig.type === tt.id && Boolean(ig.pay_what_you_can_price)
    )?.pay_what_you_can_price ??
    pwycPriceManager.getPwycPrice(
      tt.id,
      enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id
    ) ??
    null;

  const [pwycPrice, setPwycPrice] = useState<string | null>(customPrice);

  // This controls the quantity amount displayed to the user, changed by this component and by the basket.
  const [amountSelectedDisplay, setAmountSelectedDisplay] = useState(
    ticketsInBasketQuantity
  );

  const componentDidMount = useRef(false);
  const allowRequest = useRef(true);

  useEffect(() => {
    // Change the displayed quantity when the basket quantity changes.
    setAmountSelectedDisplay(ticketsInBasketQuantity);
  }, [ticketsInBasketQuantity]);

  useEffect(() => {
    // Change the displayed quantity when this component requests a change.
    if (allowRequest.current) {
      setAmountSelectedDisplay(amountRequested.amount ?? 0);
    }
  }, [amountRequested]);

  useEffect(() => {
    if (typeof onAmountRequested === "function" && !withDebounce) {
      onAmountRequested(tt, amountRequested.amount);
    }
  }, [amountRequested.amount]);

  useEffect(() => {
    if (!componentDidMount.current) {
      // do nothing on the first component render
      componentDidMount.current = true;
      return;
    }

    // if an `onAmountRequested` function is passed, override remaining logic
    if (typeof onAmountRequested === "function") {
      // have to nest this check to prevent adding to cart
      if (withDebounce) {
        onAmountRequested(tt, debouncedAmountRequested.amount);
      }
      return;
    }

    // prevent this api call from firing automatically when the cart expires and item amount is reset to 0
    if (allowRequest.current) {
      const kwargs = { initializeIfExpired: true, discountCode: null };
      if (userVouchers) {
        if (!tt.$voucher) {
          tt.$voucher = voucherForTT(tt, userVouchers); // eslint-disable-line
        }
        if (tt.$voucher) {
          kwargs.discountCode = tt.$voucher.code;
        }
      }

      // if a custom package parent ticket is selected and the quantity is over 0,
      // do not add to the basket because the user must select package options first
      if (tt.is_custom_package && debouncedAmountRequested.amount > 0) {
        onCustomPackageSelect?.(tt.id, debouncedAmountRequested.amount);
        return;
      }

      modifyItems(
        {
          item: tt,
          quantity: debouncedAmountRequested.amount,
          pay_what_you_can_price: pwycPrice,
        },
        {
          is_waitlist: isTicketTypeWaitlisted(tt) && isTicketTypeSoldOut(tt), // if sold out and waitlist is available, add to waitlist,
          hold_slug: dynamicHoldSlug,
        }
      )
        .then((returnedBasket) => {
          if (!enableAnalytics || !returnedBasket) {
            return;
          }

          // Perform analytics in isolation; Allow quantity mechanisms to continue
          try {
            const spAddToCartCall = () => {
              logAppInfo("spAddToCartCall");
              // Only track analytics for public events -> dont need tracking on box office as of this comment
              if (returnedBasket && enableTracking) {
                UserInteractionLogger.addToCart({
                  item: {
                    item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id,
                    type: tt.id,
                  },
                  count: debouncedAmountRequested.amount,
                  venue: venue?.name,
                  basket: returnedBasket,
                  event: eventData,
                });
              }
            };
            logAppInfo(`calling trackItemsInCart ${returnedBasket?.id}`);
            trackItemsInCart(
              debouncedAmountRequested.amount,
              returnedBasket,
              {
                itemId: tt.id,
                itemVariant: tt.name,
                itemName: eventData.name,
                itemType: enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id,
              },
              venue,
              analytics,
              spAddToCartCall,
              { uuid: returnedBasket[X_SP_UUID] }
            );
          } catch (e) {
            logAppInfo(
              `Returned basket truthiness: ${Boolean(returnedBasket)}`
            );
            recordAppError(e);
          }
        })
        .catch((err) => {
          // On error, revert displayed amount to basket amount
          setAmountSelectedDisplay(ticketsInBasketQuantity);
          allowRequest.current = false;
          setAmountRequested({ amount: ticketsInBasketQuantity });
          logAppInfo("Error changing ticket type quantity");
          recordAppError(err);

          console.error(err); //eslint-disable-line
        });
    } else {
      allowRequest.current = true;
    }
  }, [debouncedAmountRequested, pwycPrice]); //eslint-disable-line

  const hasAllInPricing = venue?.all_in_pricing_is_enabled;

  const venueId =
    typeof venue === "object" && venue !== null && "id" in venue
      ? (venue as { id?: number }).id
      : undefined;

  const shouldUsePaymentConfigEndpoint =
    Boolean(SWITCHES?.enable_event_page_cache) && Boolean(venueId);
  const { usePublicVenuePaymentConfig } = usePublicVenueService();
  const { data: venuePaymentConfig } = usePublicVenuePaymentConfig(venueId, {
    enabled: shouldUsePaymentConfigEndpoint,
  });

  const legacyPublishableApiKey =
    typeof venue === "object" &&
    venue !== null &&
    "publishable_api_key" in venue
      ? (venue as { publishable_api_key?: string }).publishable_api_key
      : undefined;

  const legacyAccessGrantedAccountId =
    typeof venue === "object" &&
    venue !== null &&
    "access_granted_account_id" in venue
      ? (venue as { access_granted_account_id?: string | null })
          .access_granted_account_id
      : undefined;

  const publishableApiKey = shouldUsePaymentConfigEndpoint
    ? venuePaymentConfig?.publishable_api_key
    : legacyPublishableApiKey;

  const accessGrantedAccountId = shouldUsePaymentConfigEndpoint
    ? venuePaymentConfig?.access_granted_account_id
    : legacyAccessGrantedAccountId;

  // gets the item's base price or the all-in-pricing value
  const getItemPaymentMethodPrice = (paymentMethodId: number): string => {
    let returnPrice = tt.price.toString();

    if (
      hasAllInPricing &&
      tt.fees_pricing_info &&
      tt.fees_pricing_info[purchaseSourcePlatform]
    ) {
      returnPrice =
        tt.fees_pricing_info[purchaseSourcePlatform][
          paymentMethodId
        ]?.total_price.toString();
    }

    return returnPrice || tt.price.toString();
  };

  const withPublishableApiKey = Boolean(publishableApiKey);

  const affirmItemPrice = getItemPaymentMethodPrice(
    PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.id
  );

  const isAffirmMessageEnabled =
    enableAffirm &&
    withPublishableApiKey &&
    !hasPaymentPlans &&
    venue?.affirm_is_enabled &&
    // Affirm also has a minimum requirement of $50, enforced by the integration itself.
    Number(affirmItemPrice) >= PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.minAmount &&
    Number(affirmItemPrice) <= PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.maxAmount;

  const displayDescription = Boolean(tt.description) && !hideDescription;

  const handleQuantitySelect = (amount: number) => {
    setAmountRequested({ amount });
  };

  const handleSetPwycPrice = useCallback(
    (value: string) => {
      setPwycPrice(value);
      pwycPriceManager.setPwycPrice(
        tt.id,
        value,
        enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id
      );
    },
    [tt.id]
  );

  if (
    !displayTicketType(
      tt,
      eventData?.is_password_protected,
      ticketsAuthorizedByPassword
    )
  ) {
    return null;
  }

  const bem = componentName("ticket-types-container");

  return (
    <DetailCardContainer
      key={tt.id}
      isModal={isModal}
      data-testid="ticket-types-card-container"
      border="none"
      background="transparent"
      padding="none"
      className={bem.className()}
    >
      <SaleStartsOnBadge
        saleStartsOn={tt.sale_starts_on}
        isOnSale={isOnSaleTicketType(tt)}
        timezone={eventData?.timezone}
        mb="xs"
        className={bem.element("badge", { saleStartsOn: true })}
      />
      <ItemQuantitySelectInfo
        alwaysBelow={quantityButtonsAlwaysBelow}
        id={tt.id}
        showQuantityButtons={
          showTicketButtons(
            tt,
            eventData,
            userVouchers,
            ticketAuthArray,
            updateTicketAuthArray,
            basketVoucherCode,
            !!dynamicHoldSlug
          ) ||
          (!isSoldOut && ticketsInBasketQuantity > 0)
        }
        itemTitle={tt.name}
        itemTitleAccessory={titleAccessory}
        itemSubtitle={isRecurring ? eventData.name : ""} // display event name as subtitle for recurring events
        itemPrice={tt.price.toString()}
        currency={
          "currency" in eventData ? eventData?.currency : venue?.currency
        }
        feesPricingInfo={tt.fees_pricing_info}
        hasAssignedSeating={
          // We type assert here as PublicBaseEventSerializer marks 'assigned_space' as an optional property, thus narrowing via JS 'in' does not fully work.
          !!(eventData as PublicBaseEventSerializer)?.assigned_space ||
          !!(eventData as PublicCalendarEventDetailsSerializer)
            ?.assigned_space_id
        }
        hasAllInPricing={hasAllInPricing}
        customPackageData={getCustomPackageData()}
        soldOut={isSoldOut}
        amountSelected={amountSelectedDisplay}
        minSelectionAmount={purchaseLimits?.min || tt.minimum_purchase_limit}
        maxSelectionAmount={purchaseLimits?.max || tt.purchase_limit}
        openFrontendDetailsUrl={openFrontendDetailsUrl}
        isLoading={isLoading}
        inventoryComponent={
          /* Display low inventory or remaining tickets badge */
          <InventoryBadge tt={tt} eventData={eventData} venueData={venue} />
        }
        onAmountSelectedChange={handleQuantitySelect}
        livestreamAccess={tt.issues_virtual_stream_access}
        isCalendar={isCalendar}
        isWaitlistTicket={isTicketTypeWaitlisted(tt)}
        frontendDetailsUrl={eventData.frontend_details_url}
        autoDiscount={tt.auto_discount}
        isOnSale={isOnSaleTicketType(tt)}
        isPatronItemSelection={isPatronItemSelection}
        startingFromPrice={startingFromPrice}
        ticketTypesPatronTypeCount={ticketTypesPatronTypeCount}
        pwycConfig={tt?.pay_what_you_can_configuration}
        onSetPwycPrice={handleSetPwycPrice}
        pwycPrice={pwycPrice}
        forceQuantityButtons={forceQuantityButtons}
        isPackageParent={tt?.is_bundle}
        onEventSelectCallback={onEventSelectCallback}
      >
        {itemQuantitySelectInfoChildren}
      </ItemQuantitySelectInfo>

      {isAffirmMessageEnabled && !isTicketTypeWaitlisted(tt) && (
        <StripeAffirmText
          publishableApiKey={publishableApiKey}
          accessGrantedAccountId={accessGrantedAccountId}
          itemPrice={affirmItemPrice}
          currency={enums.CURRENCY_MAP.USD.key}
          isSoldOut={isSoldOut}
        />
      )}
      <Box opacity={isSoldOut && !isTicketTypeWaitlisted(tt) ? "0.3" : "1.0"}>
        <PaymentVerbiage
          hasPaymentPlan={hasPaymentPlans}
          eventStartsOn={eventData.starts_on}
          eventEndsOn={eventData.ends_on}
          customStartsOn={tt.custom_ticket_event_starts_on}
          customEndsOn={tt.custom_ticket_event_ends_on}
          timezone={eventData.timezone}
          ticketVoucherData={{
            type: tt,
            basketVoucherCode,
            userVouchers,
            eventUrl: eventData?.frontend_details_url,
            venueSlug: venue?.slug,
            venueName: venue?.name,
          }}
          isOnSale={isOnSaleTicketType(tt)}
        />
        {!isSoldOut ? (
          <ItemPasswordEntry
            item={tt}
            slugToCheck={eventData?.slug}
            authArray={ticketAuthArray}
            updateAuthArray={updateTicketAuthArray}
          />
        ) : (
          <></>
        )}

        {displayDescription && (
          <ReadMore
            descriptionHtml={tt.description}
            openReadMoreText={t("Read More")}
            closeReadMoreText={t("Show Less")}
          />
        )}
      </Box>
    </DetailCardContainer>
  );
};

TicketTypesContainer.Skeleton = function TicketTypesContainerSkeleton() {
  return (
    <Flex direction="column" gap="md" width="100%">
      <Skeleton width="element.7xl" height="element.xl" />

      <Flex direction="row" justifyContent="space-between">
        <Flex direction="column" gap="xs">
          <Skeleton width="element.4xl" height="element.xl" />
          <Skeleton width="element.5xl" height="element.sm" />
        </Flex>
        <Skeleton width="element.8xl" height="element.4xl" />
      </Flex>
    </Flex>
  );
};

export default TicketTypesContainer;
